import { FullPageLoader, Typography } from '@analytical-alley/ui';
import { useMutation } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';

const route = getRouteApi('/invite/accept/$inviteId/');

let isFirst = true;
const DOUBLE_CALL_ERROR_MESSAGE = 'Function was already called';

const acceptInvite = async (
    inviteId: string,
): Promise<{ projectId: string }> => {
    if (!isFirst) {
        return Promise.reject(new Error(DOUBLE_CALL_ERROR_MESSAGE));
    }

    isFirst = false;
    return fetch(`/v1/invite/${inviteId}/accept`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
    })
        .then((response) => {
            if (!response.ok) {
                return response.json().then((data) => {
                    throw data;
                });
            }
            return response;
        })
        .then((response) => response.json());
};

export const InviteAcceptPage = () => {
    const { inviteId } = route.useParams();
    const navigate = useNavigate();

    const navigateToProject = useCallback(
        async (projectId: string) => {
            await navigate({
                to: '/project/$projectId',
                params: {
                    projectId,
                },
                replace: true,
            });
        },
        [navigate],
    );

    const { mutate, isError, error } = useMutation({
        mutationFn: acceptInvite,
        mutationKey: ['acceptInvite', inviteId],
        onSuccess: async (data) => {
            await navigateToProject(data.projectId);
        },
        onError: async (data: {
            statusCode?: number;
            projectId?: string;
            message: string;
            timestamp: string;
            path: string;
        }) => {
            if (data?.statusCode === 409 && !!data?.projectId) {
                await navigateToProject(data?.projectId);
            }
        },
    });

    useEffect(() => {
        mutate(inviteId);
    }, [inviteId, mutate]);

    if (isError && error.message !== DOUBLE_CALL_ERROR_MESSAGE) {
        return (
            <div className="flex justify-center px-40 p-16 lg:p-36 pb-48 dark:text-white max-lg:px-5">
                <Typography variant="titleS" className="mb-4">
                    Error: {error.message}
                </Typography>
            </div>
        );
    }

    return <FullPageLoader />;
};
